import { Component } from '@theme/component';

/**
 * Tabs per area geografica (desktop, via #selectArea) — su mobile le tab sono
 * nascoste via CSS e ogni area diventa un accordion di primo livello, chiuso
 * di default, aperto/chiuso dal bottone `.shipping-area__mobile-toggle` (vedi
 * #toggleMobileArea). Nessun breakpoint-check in JS: il bottone mobile è
 * `display:none` su desktop (CSS in sections/everart-shipping-faq.liquid),
 * quindi #toggleMobileArea non è mai raggiungibile fuori da ≤749px.
 *
 * @typedef {Object} ShippingFaqComponentRefs
 */

/**
 * @extends {Component<ShippingFaqComponentRefs>}
 */
class ShippingFaq extends Component {
  connectedCallback() {
    super.connectedCallback?.();
    // Il primo bottone/pannello attivo è deciso qui in JS, non in Liquid:
    // ogni blocco "_shipping-area" emette il proprio bottone+pannello in modo
    // indipendente (vedi blocks/_shipping-area.liquid), quindi non esiste un
    // forloop.first cross-blocco affidabile lato Liquid per marcare il primo.
    const firstTab = this.querySelector('.everart-shipping-faq__tab');
    if (firstTab) {
      firstTab.classList.add('is-active');
      firstTab.setAttribute('aria-selected', 'true');
    }
    const firstArea = this.querySelector('.shipping-area');
    if (firstArea) firstArea.classList.add('is-active');

    this.#injectFaqSchema();
  }

  /**
   * Costruisce e inietta il FAQPage JSON-LD leggendo domande/risposte dal DOM
   * già renderizzato, invece che dalle settings Liquid dei blocchi.
   *
   * Motivo: con il rendering a blocchi annidati via `content_for 'blocks'`,
   * ogni blocco _shipping-area / accordion / _accordion-row si renderizza in
   * isolamento — il genitore (questa sezione) non ha accesso affidabile a
   * `block.settings` o `block.blocks` dei discendenti (verificato empiricamente
   * il 2026-07-17: erano sempre vuoti/zero pur avendo contenuto visibile).
   * L'unico dato certo e sempre aggiornato è quello che l'utente vede — quindi
   * lo schema si costruisce da lì. Bonus: non può mai andare fuori sync col
   * contenuto realmente mostrato, a differenza di uno schema scritto a mano.
   *
   * Legge tutte le `.shipping-area` (tutte le categorie, non solo quella
   * attiva: esistono sempre nel DOM, solo nascoste via CSS su desktop).
   */
  #injectFaqSchema() {
    const items = [];
    this.querySelectorAll('.shipping-area .details').forEach((details) => {
      const header = details.querySelector('.details__header');
      if (!header) return;
      const headerClone = /** @type {HTMLElement} */ (header.cloneNode(true));
      headerClone.querySelectorAll('svg, .svg-wrapper').forEach((el) => el.remove());
      const question = headerClone.textContent?.trim();
      const answerEl = details.querySelector('rte-formatter');
      const answer = answerEl?.textContent?.trim().replace(/\s+/g, ' ');
      if (question && answer) {
        items.push({
          '@type': 'Question',
          name: question,
          acceptedAnswer: { '@type': 'Answer', text: answer },
        });
      }
    });
    if (!items.length) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items,
    });
    document.head.appendChild(script);
  }

  /**
   * Attiva l'area selezionata e la relativa tab, disattiva le altre.
   *
   * NOTA: qui va usato event.target, non event.currentTarget. Il dispatcher
   * di @theme/component (assets/component.js) registra i listener on:click
   * una sola volta su `document` (event delegation) — durante l'esecuzione
   * del callback, il vero `currentTarget` nativo è quindi sempre `document`
   * stesso, non il bottone. `event.target` invece è risolto correttamente al
   * bottone dal dispatcher tramite un Proxy (vedi getElement() in
   * component.js). Con currentTarget il metodo usciva subito per OGNI click
   * (silenziosamente, `tab instanceof HTMLElement` era sempre falso) — bug
   * reale trovato il 2026-07-09, non solo su "alcune" tab: su nessuna, la
   * prima sembrava funzionare solo perché già attiva di default.
   * @param {MouseEvent} event
   */
  selectArea = (event) => {
    const tab = /** @type {EventTarget | null} */ (event.target);
    if (!(tab instanceof HTMLElement)) return;
    const button = tab.closest('.everart-shipping-faq__tab');
    if (!(button instanceof HTMLElement)) return;
    const areaId = button.dataset.shippingTab;
    if (!areaId) return;

    for (const otherTab of this.querySelectorAll('.everart-shipping-faq__tab')) {
      const isActive = otherTab === button;
      otherTab.classList.toggle('is-active', isActive);
      otherTab.setAttribute('aria-selected', String(isActive));
    }

    for (const area of this.querySelectorAll('.shipping-area')) {
      area.classList.toggle('is-active', area.id === `shipping-area-${areaId}`);
    }
  };

  /**
   * Mobile-only (≤749px): apre/chiude le domande della categoria cliccata,
   * chiudendo automaticamente le altre (fisarmonica esclusiva). Stesso
   * pattern .closest()/event.target di #selectArea sopra e stesso motivo
   * (event delegation su document, va usato event.target non currentTarget).
   *
   * Click sulla categoria già aperta la richiude (nessun requisito lo vieta,
   * comportamento standard per un accordion FAQ).
   * @param {MouseEvent} event
   */
  toggleMobileArea = (event) => {
    const target = /** @type {EventTarget | null} */ (event.target);
    if (!(target instanceof HTMLElement)) return;
    const button = target.closest('.shipping-area__mobile-toggle');
    if (!(button instanceof HTMLElement)) return;

    const wasOpen = button.getAttribute('aria-expanded') === 'true';

    for (const otherButton of this.querySelectorAll('.shipping-area__mobile-toggle')) {
      otherButton.setAttribute('aria-expanded', 'false');
    }

    button.setAttribute('aria-expanded', String(!wasOpen));
  };
}

if (!customElements.get('shipping-faq-component')) {
  customElements.define('shipping-faq-component', ShippingFaq);
}
